import React from 'react';
import {
  Alert,
  Button,
  Col,
  Container,
  ProgressBar,
  Row,
  Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CompanyOnboardingRouteGuard from '../CompanyOnboardingRouteGuard';
import Step1BasicInfo from './Step1BasicInfo';
import Step2SalaryDetails from './Step2SalaryDetails';
import Step3Description from './Step3Description';
import Step4ApplicationMethod from './Step4ApplicationMethod';
import Step5ReviewSubmit from './Step5ReviewSubmit';
import { WIZARD_STEPS } from './wizardConstants';
import DraftRecoveryModal from './DraftRecoveryModal';
import ReactivationFeeGate from './ReactivationFeeGate';
import { JobWizardProvider, useJobWizard } from './JobWizardContext';
import { useAuth } from '../../../context/AuthContext';

function PostJobWizardContent() {
  const { token } = useAuth();
  const wizard = useJobWizard();
  const progress = (wizard.step / WIZARD_STEPS.length) * 100;

  if (wizard.loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" data-testid="rwm-wizard-loading">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </Container>
    );
  }

  if (wizard.reactivationRequired) {
    return (
      <ReactivationFeeGate
        token={token}
        fee={wizard.reactivationFee}
        onCleared={wizard.refreshReactivationStatus}
      />
    );
  }

  if (wizard.companyUnapproved) {
    return (
      <Container className="py-5" data-testid="rwm-wizard-unapproved">
        <Alert variant="warning" className="mb-3">
          Your company profile is pending admin approval. Job posting will be available once an admin
          approves your company.
        </Alert>
        <Link to="/company/dashboard" className="btn btn-outline-secondary btn-sm">
          Back to dashboard
        </Link>
      </Container>
    );
  }

  if (wizard.roleDenied) {
    return (
      <Container className="py-5" data-testid="rwm-wizard-access-denied">
        <Alert variant="danger" className="mb-3">
          Your role does not allow managing job postings.
        </Alert>
        <Link to="/company/dashboard" className="btn btn-outline-secondary btn-sm">
          Back to dashboard
        </Link>
      </Container>
    );
  }

  return (
    <Container className="py-4 rwm-wizard" data-testid="rwm-wizard">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h1 className="h3 mb-0 rwm-wizard__title">Post a Job</h1>
        <Link to="/company/dashboard" className="btn btn-outline-secondary btn-sm" data-testid="rwm-wizard-cancel">
          Back to dashboard
        </Link>
      </div>

      {wizard.initError ? (
        <Alert variant="danger" data-testid="rwm-wizard-init-error">
          {wizard.initError}
        </Alert>
      ) : null}

      <ProgressBar now={progress} className="rwm-wizard__progress mb-4" data-testid="rwm-wizard-progress" />

      <Row>
        <Col lg={3} className="mb-4 mb-lg-0">
          <nav className="rwm-wizard__nav" aria-label="Wizard steps">
            {WIZARD_STEPS.map((s) => (
              <div
                key={s.key}
                className={`rwm-wizard__nav-item ${wizard.step === s.key ? 'rwm-wizard__nav-item--active' : ''} ${wizard.step > s.key ? 'rwm-wizard__nav-item--done' : ''}`}
                data-testid={`rwm-wizard-nav-step-${s.key}`}
              >
                <span className="rwm-wizard__nav-number">{s.key}</span>
                <span className="rwm-wizard__nav-label">{s.label}</span>
              </div>
            ))}
          </nav>
          {wizard.lastSavedAt ? (
            <p className="text-muted small mt-3 mb-0" data-testid="rwm-wizard-last-saved">
              Draft saved {wizard.lastSavedAt.toLocaleTimeString()}
            </p>
          ) : null}
          {wizard.saveError ? (
            <p className="text-danger small mt-2 mb-0" data-testid="rwm-wizard-save-error">
              {wizard.saveError}
            </p>
          ) : null}
        </Col>

        <Col lg={9}>
          <div className="rwm-wizard__panel">
            {wizard.step === 1 ? <Step1BasicInfo /> : null}
            {wizard.step === 2 ? <Step2SalaryDetails /> : null}
            {wizard.step === 3 ? <Step3Description /> : null}
            {wizard.step === 4 ? <Step4ApplicationMethod /> : null}
            {wizard.step === 5 ? <Step5ReviewSubmit /> : null}

            <div className="rwm-wizard__actions d-flex justify-content-between mt-4 pt-3 border-top">
              <Button
                variant="outline-secondary"
                disabled={wizard.step === 1 || wizard.saving || wizard.submitting}
                onClick={wizard.goBack}
                data-testid="rwm-wizard-back"
              >
                Back
              </Button>
              {wizard.step < 5 ? (
                <Button
                  variant="info"
                  disabled={wizard.saving}
                  onClick={wizard.goNext}
                  data-testid="rwm-wizard-next"
                >
                  {wizard.saving ? 'Saving…' : 'Next'}
                </Button>
              ) : null}
            </div>
          </div>
        </Col>
      </Row>

      <DraftRecoveryModal
        show={wizard.showRecovery}
        draft={wizard.recoveryDraft}
        onContinue={wizard.handleContinueDraft}
        onStartFresh={wizard.handleStartFresh}
      />
    </Container>
  );
}

export default function PostJobWizard() {
  return (
    <CompanyOnboardingRouteGuard>
      <JobWizardProvider>
        <PostJobWizardContent />
      </JobWizardProvider>
    </CompanyOnboardingRouteGuard>
  );
}
